import { Module } from '../../types';

export const dspyModule4: Module = {
  id: 'dspy-module4',
  title: 'DSPy Optimization',
  description:
    'Master DSPy optimizers including few-shot learning, instruction optimization, and advanced techniques like MIPROv2 and ensemble methods.',
  duration: '5-6 hours',
  isUnderDevelopment: true,
  content: `# Module 4: DSPy Optimization

## 🎯 Learning Objectives

By the end of this module, you will:
- Understand what DSPy optimizers tune and how they work
- Master few-shot learning optimizers (LabeledFewShot, BootstrapFewShot, KNNFewShot)
- Apply instruction optimization techniques (COPRO, MIPROv2)
- Use advanced optimization methods (BootstrapFinetune, Ensemble, BetterTogether)
- Choose the right optimizer for different tasks and contexts
- Save and load optimized programs effectively
- Understand cost and time considerations in optimization

## 🔧 Understanding DSPy Optimizers

### What Optimizers Tune

DSPy optimizers automatically improve your programs by tuning three key components:

1. **Prompts/Instructions**: The natural language instructions given to language models
2. **Few-shot examples**: Demonstrations of desired input-output behavior
3. **Weights**: For fine-tunable models (when applicable)

### The Optimization Revolution

Traditional prompt engineering is:
- **Manual**: Write, test, tweak, repeat
- **Fragile**: Small changes can break everything
- **Time-consuming**: Days or weeks of iteration
- **Inconsistent**: Different engineers get different results

DSPy optimization is:
- **Automatic**: Algorithms find optimal configurations
- **Robust**: Systematically improves across examples
- **Fast**: Hours instead of weeks
- **Reproducible**: Same data + optimizer = same results

### The Optimization Workflow

\`\`\`python
# Complete optimization workflow
import dspy
from dspy.teleprompt import BootstrapFewShot

# 1. Define your program
class QAProgram(dspy.Module):
    def __init__(self):
        self.qa = dspy.ChainOfThought("question -> answer")
    
    def forward(self, question):
        return self.qa(question=question)

# 2. Create training/validation split
trainset = training_data[:100]  # For optimization
valset = training_data[100:150]  # For validation

# 3. Define your metric
def accuracy_metric(gold, pred, trace=None):
    return gold.answer.lower() == pred.answer.lower()

# 4. Choose and configure optimizer
optimizer = BootstrapFewShot(
    metric=accuracy_metric,
    max_bootstrapped_demos=4,
    max_labeled_demos=16
)

# 5. Compile (optimize) your program
optimized_program = optimizer.compile(
    student=QAProgram(),
    trainset=trainset
)

# 6. Evaluate on validation set
evaluate = dspy.Evaluate(devset=valset, metric=accuracy_metric)
result = evaluate(optimized_program)
print(f"Accuracy: {result}")
\`\`\`

### Training vs. Validation Data Splits

\`\`\`python
# Proper data splitting for optimization
full_dataset = load_your_data()  # Your complete dataset

# Split ratios: 60% train, 20% dev, 20% test
train_size = int(0.6 * len(full_dataset))
dev_size = int(0.2 * len(full_dataset))

trainset = full_dataset[:train_size]           # For optimizer
devset = full_dataset[train_size:train_size+dev_size]  # For evaluation
testset = full_dataset[train_size+dev_size:]   # For final testing

print(f"Train: {len(trainset)}, Dev: {len(devset)}, Test: {len(testset)}")
\`\`\`

### Cost and Time Considerations

\`\`\`python
# Optimization can be expensive - plan accordingly

# Cost factors:
# - Number of training examples
# - Number of optimization rounds
# - Complexity of your program
# - Choice of language model

# Time-saving strategies:
optimizer = BootstrapFewShot(
    metric=accuracy_metric,
    max_bootstrapped_demos=2,  # Reduce for faster optimization
    max_labeled_demos=8,       # Fewer examples = less cost
    max_rounds=1               # Single round for quick iteration
)

# Monitor optimization progress
with dspy.context(show_guidelines=False):
    optimized = optimizer.compile(student, trainset=small_trainset)
\`\`\`

### Optimization Cost Calculator

\`\`\`python
def estimate_optimization_cost(
    optimizer_type,
    dataset_size,
    model,
    num_trials=None
):
    """Estimate the cost and time for optimization."""
    
    # Base cost multipliers by optimizer type
    base_costs = {
        'BootstrapFewShot': dataset_size * 2,      # Bootstrap + validation
        'MIPROv2_light': dataset_size * 5,         # More proposals
        'MIPROv2_medium': dataset_size * 15,       # More trials
        'MIPROv2_heavy': dataset_size * 40,        # Extensive search
        'COPRO': dataset_size * 10,                # Multiple iterations
        'LabeledFewShot': dataset_size * 1,        # Single pass
    }
    
    # Model pricing (example rates)
    model_pricing = {
        'gpt-4': 0.03,        # per 1k tokens
        'gpt-4o-mini': 0.0015,
        'gpt-3.5-turbo': 0.001,
        'claude-3-haiku': 0.00025,
    }
    
    # Token estimation
    tokens_per_example = 500  # Average
    cost_per_1k_tokens = model_pricing.get(model, 0.001)
    
    total_tokens = base_costs.get(optimizer_type, dataset_size * 5) * tokens_per_example
    total_cost = (total_tokens / 1000) * cost_per_1k_tokens
    
    return {
        'estimated_cost': f"$" + f"{total_cost:.2f}",
        'estimated_time': f"{total_tokens / 50000:.1f} hours",  # 50k tokens/hour estimate
        'total_api_calls': base_costs.get(optimizer_type, dataset_size * 5)
    }

# Example usage
cost_estimate = estimate_optimization_cost(
    'MIPROv2_medium',
    dataset_size=200,
    model='gpt-4o-mini'
)
print(f"Estimated cost: {cost_estimate['estimated_cost']}")
print(f"Estimated time: {cost_estimate['estimated_time']}")
print(f"Total API calls: {cost_estimate['total_api_calls']}")
\`\`\`

### Monitoring Optimization Progress

\`\`\`python
import matplotlib.pyplot as plt
import numpy as np
from datetime import datetime

class OptimizationMonitor:
    """Real-time monitoring of optimization progress."""
    
    def __init__(self):
        self.scores = []
        self.configs = []
        self.timestamps = []
        self.start_time = datetime.now()
    
    def update(self, trial, score, config):
        """Record a new optimization result."""
        self.scores.append(score)
        self.configs.append(config)
        self.timestamps.append(datetime.now())
        
        # Print progress
        elapsed = (datetime.now() - self.start_time).total_seconds()
        print(f"Trial {trial}: Score={score:.3f}, Time={elapsed:.1f}s")
        
        # Update visualization if in notebook
        if len(self.scores) % 5 == 0:  # Update every 5 trials
            self.plot_progress()
    
    def plot_progress(self):
        """Visualize optimization progress."""
        plt.figure(figsize=(12, 4))
        
        # Score progression
        plt.subplot(1, 3, 1)
        plt.plot(self.scores, 'b-o', markersize=4)
        plt.xlabel('Trial')
        plt.ylabel('Score')
        plt.title('Optimization Progress')
        plt.grid(True, alpha=0.3)
        
        # Best score over time
        plt.subplot(1, 3, 2)
        best_scores = np.maximum.accumulate(self.scores)
        plt.plot(best_scores, 'g-', linewidth=2)
        plt.xlabel('Trial')
        plt.ylabel('Best Score So Far')
        plt.title('Best Score Evolution')
        plt.grid(True, alpha=0.3)
        
        # Score distribution
        plt.subplot(1, 3, 3)
        plt.hist(self.scores, bins=20, alpha=0.7, color='orange')
        plt.xlabel('Score')
        plt.ylabel('Frequency')
        plt.title('Score Distribution')
        
        plt.tight_layout()
        plt.show()
    
    def get_best_config(self):
        """Return the best configuration found."""
        if not self.scores:
            return None
        best_idx = np.argmax(self.scores)
        return {
            'score': self.scores[best_idx],
            'config': self.configs[best_idx],
            'trial': best_idx + 1
        }

# Usage with optimization
monitor = OptimizationMonitor()

# Hook into optimizer (example with custom wrapper)
def monitored_compile(optimizer, program, trainset, monitor):
    # This is a conceptual example - actual implementation varies by optimizer
    original_evaluate = optimizer._evaluate_candidate
    
    def wrapped_evaluate(candidate, trial_num):
        score = original_evaluate(candidate)
        monitor.update(trial_num, score, candidate)
        return score
    
    optimizer._evaluate_candidate = wrapped_evaluate
    return optimizer.compile(program, trainset)
\`\`\`

## 📚 Few-Shot Learning Optimizers

### LabeledFewShot: Using Manual Examples

The simplest optimizer that uses your provided examples directly:

\`\`\`python
from dspy.teleprompt import LabeledFewShot

# Use manual examples as few-shot demonstrations
optimizer = LabeledFewShot(k=8)  # Use 8 examples per prompt

# Your training examples become few-shot examples
labeled_examples = [
    dspy.Example(question="What is the capital of France?", answer="Paris").with_inputs("question"),
    dspy.Example(question="What is 2+2?", answer="4").with_inputs("question"),
    # ... more examples
]

optimized = optimizer.compile(
    student=my_program,
    trainset=labeled_examples
)
\`\`\`

### BootstrapFewShot: Self-Generating Examples

Automatically generates high-quality examples from your training data:

\`\`\`python
from dspy.teleprompt import BootstrapFewShot

# Most popular and effective optimizer
optimizer = BootstrapFewShot(
    metric=accuracy_metric,           # How to measure success
    max_bootstrapped_demos=4,         # Max generated examples per stage
    max_labeled_demos=16,             # Max manual examples to use
    max_rounds=1,                     # Optimization rounds
    teacher=None                      # Use same model as student
)

# The optimizer will:
# 1. Use your program to generate candidate examples
# 2. Filter examples that pass your metric
# 3. Select the best examples for few-shot prompting
optimized = optimizer.compile(
    student=my_program,
    trainset=trainset
)
\`\`\`

### Understanding Bootstrap Quality

\`\`\`python
def analyze_bootstrap_quality(optimizer, program, trainset):
    """Analyze the quality of bootstrapped examples."""
    
    # Track bootstrapping success
    success_count = 0
    total_attempts = 0
    examples_by_score = []
    
    for example in trainset:
        total_attempts += 1
        
        # Try to bootstrap
        prediction = program(**example.inputs())
        score = optimizer.metric(example, prediction)
        
        examples_by_score.append({
            'example': example,
            'prediction': prediction,
            'score': score,
            'success': score > 0.5
        })
        
        if score > 0.5:
            success_count += 1
    
    # Analysis
    bootstrap_rate = success_count / total_attempts
    print(f"Bootstrap Success Rate: {bootstrap_rate:.2%}")
    print(f"Successful Examples: {success_count}/{total_attempts}")
    
    # Show best and worst examples
    examples_by_score.sort(key=lambda x: x['score'], reverse=True)
    
    print("\\nBest Bootstrapped Example:")
    best = examples_by_score[0]
    print(f"Score: {best['score']:.3f}")
    print(f"Input: {best['example'].inputs()}")
    print(f"Output: {best['prediction']}")
    
    print("\\nWorst Bootstrapped Example:")
    worst = examples_by_score[-1]
    print(f"Score: {worst['score']:.3f}")
    print(f"Input: {worst['example'].inputs()}")
    print(f"Output: {worst['prediction']}")
    
    return examples_by_score

# Advanced: Using a teacher model
teacher_program = my_program.deepcopy()
dspy.settings.configure(lm=dspy.LM('gpt-4'))  # Stronger model as teacher

optimizer = BootstrapFewShot(
    metric=accuracy_metric,
    teacher=teacher_program,  # Use GPT-4 to generate examples
    teacher_settings={'temperature': 0.7}
)

# Compile with student model
dspy.settings.configure(lm=dspy.LM('gpt-3.5-turbo'))  # Cheaper student model
optimized = optimizer.compile(student=my_program, trainset=trainset)
\`\`\`

### BootstrapFewShotWithRandomSearch

Adds random search to BootstrapFewShot for better exploration:

\`\`\`python
from dspy.teleprompt import BootstrapFewShotWithRandomSearch

# Enhanced version with random search
optimizer = BootstrapFewShotWithRandomSearch(
    metric=accuracy_metric,
    max_bootstrapped_demos=4,
    max_labeled_demos=16,
    num_candidate_programs=10,        # Try multiple random combinations
    num_threads=4                     # Parallel evaluation
)

optimized = optimizer.compile(student=my_program, trainset=trainset)
\`\`\`

### KNNFewShot: Nearest Neighbor Selection

Selects examples most similar to the current input:

\`\`\`python
from dspy.teleprompt import KNNFewShot

# Dynamic example selection based on similarity
optimizer = KNNFewShot(
    k=5,                             # Number of examples to retrieve
    trainset=trainset               # Examples to search through
)

# At inference time, finds most relevant examples
optimized = optimizer.compile(student=my_program, trainset=trainset)

# Example usage:
result = optimized(question="What is machine learning?")
# Automatically finds and uses 5 most similar examples
\`\`\`

## 🎯 Instruction Optimization

### COPRO: Coordinate Ascent for Prompts

Optimizes the actual instructions/prompts in your signatures:

\`\`\`python
from dspy.teleprompt import COPRO

# Automatically improves your prompt instructions
optimizer = COPRO(
    metric=accuracy_metric,
    breadth=10,                      # Number of prompt variations to try
    depth=3,                         # Optimization depth
    init_temperature=1.4,            # Creativity in prompt generation
    verbose=True                     # Show optimization progress
)

# COPRO will rewrite your signature instructions
# Original: "question -> answer"
# Optimized: "Given the question, provide a comprehensive and accurate answer -> answer"
optimized = optimizer.compile(student=my_program, trainset=trainset)

# View the optimized instructions
print(optimized.qa.signature.instructions)
\`\`\`

### MIPROv2: Data-Aware Instruction and Demonstration Optimization

The most sophisticated optimizer - optimizes both instructions and examples:

\`\`\`python
from dspy.teleprompt import MIPROv2

# Light mode - fast optimization
light_optimizer = MIPROv2(
    metric=accuracy_metric,
    num_candidates=10,               # Number of instruction candidates
    init_temperature=1.0,            # Moderate creativity
    verbose=True                     # Show progress
)

# Medium mode - balanced approach  
medium_optimizer = MIPROv2(
    metric=accuracy_metric,
    num_candidates=20,
    init_temperature=1.2,
    verbose=True
)

# Heavy mode - most thorough optimization
heavy_optimizer = MIPROv2(
    metric=accuracy_metric,
    num_candidates=25,               # More candidates = better results
    init_temperature=1.4,            # Higher creativity
    verbose=True
)

# MIPROv2 features:
# - Zero-shot optimization (no examples needed)
# - Bayesian optimization approach
# - Data-aware instruction generation
optimized = heavy_optimizer.compile(student=my_program, trainset=trainset)
\`\`\`

### Zero-Shot Optimization with MIPROv2

\`\`\`python
# Optimize without any training examples!
zero_shot_optimizer = MIPROv2(
    metric=accuracy_metric,
    num_candidates=15,
    init_temperature=1.3,
    verbose=True
)

# Can work with just a small validation set
optimized = zero_shot_optimizer.compile(
    student=my_program,
    trainset=[],                     # No training examples!
    valset=small_validation_set      # Just for metric evaluation
)
\`\`\`

## 🔄 Advanced Optimization Techniques

### Advanced Module Configurations

\`\`\`python
# Retry with feedback
class CodeGeneration(dspy.Signature):
    """Generate Python functions based on requirements."""
    
    requirements: str = dspy.InputField()
    
    code: str = dspy.OutputField()
    test_cases: List[str] = dspy.OutputField()

# Add retry logic with custom validation
class SmartCodeGenerator(dspy.Module):
    def __init__(self, max_retries=3):
        self.generator = dspy.ChainOfThought(CodeGeneration)
        self.max_retries = max_retries
    
    def forward(self, requirements):
        for attempt in range(self.max_retries):
            result = self.generator(requirements=requirements)
            
            # Validate the generated code
            try:
                # Test if code is syntactically valid
                compile(result.code, '<string>', 'exec')
                
                # Run test cases
                for test in result.test_cases:
                    exec(result.code + '\\n' + test)
                
                return result
            except Exception as e:
                if attempt < self.max_retries - 1:
                    # Add error feedback for next attempt
                    requirements = f"{requirements}\\n\\nPrevious attempt failed with: {str(e)}\\nPlease fix."
                else:
                    raise

code_gen = SmartCodeGenerator()
result = code_gen(requirements="Create a function to validate email addresses")
\`\`\`

### BootstrapFinetune: Weight Updates for Small Models

Optimize the actual model weights (for models you can fine-tune):

\`\`\`python
from dspy.teleprompt import BootstrapFinetune

# Requires a model you can fine-tune (like smaller T5, GPT-2, etc.)
optimizer = BootstrapFinetune(
    metric=accuracy_metric,
    epochs=3,                        # Fine-tuning epochs
    batch_size=8,                    # Training batch size
    learning_rate=5e-5               # Learning rate for fine-tuning
)

# This will actually update model weights
finetuned_program = optimizer.compile(
    student=my_program,
    trainset=trainset
)

# Note: Only works with fine-tunable models
# Won't work with API-only models like GPT-4

# Understanding the finetuning process
class FinetuningProcess:
    def __init__(self, program, trainset, metric):
        self.program = program
        self.trainset = trainset
        self.metric = metric
    
    def generate_training_data(self):
        """Bootstrap training data for finetuning."""
        
        training_data = []
        
        for example in self.trainset:
            # Get program's prediction
            prediction = self.program(**example.inputs())
            
            # Check if it's good
            if self.metric(example, prediction) > 0.5:
                # Extract module traces
                for module in self.program.modules():
                    if hasattr(module, 'last_prompt'):
                        training_data.append({
                            'prompt': module.last_prompt,
                            'completion': module.last_completion,
                            'module': module.__class__.__name__
                        })
        
        return training_data
    
    def prepare_for_finetuning(self, training_data):
        """Format data for finetuning."""
        
        formatted = []
        for item in training_data:
            formatted.append({
                'messages': [
                    {'role': 'system', 'content': 'You are a helpful assistant.'},
                    {'role': 'user', 'content': item['prompt']},
                    {'role': 'assistant', 'content': item['completion']}
                ]
            })
        
        return formatted
\`\`\`

### Ensemble: Combining Multiple Programs

Combine multiple optimized programs for better performance:

\`\`\`python
from dspy.teleprompt import Ensemble

# Create multiple optimized versions with different strategies
bootstrap_opt = BootstrapFewShot(metric=accuracy_metric)
copro_opt = COPRO(metric=accuracy_metric)
mipro_opt = MIPROv2(metric=accuracy_metric)

# Optimize with each strategy
optimized1 = bootstrap_opt.compile(my_program, trainset)
optimized2 = copro_opt.compile(my_program, trainset)
optimized3 = mipro_opt.compile(my_program, trainset)

# Create ensemble
ensemble_optimizer = Ensemble(
    programs=[optimized1, optimized2, optimized3],
    aggregation_method="majority_vote"  # or "average", "weighted"
)

# The ensemble combines predictions from all three
result = ensemble_optimizer(question="What is the capital of France?")
# Returns the majority vote or averaged result
\`\`\`

### BetterTogether: Composing Optimizers

Combine different optimization strategies in sophisticated ways:

\`\`\`python
from dspy.teleprompt import BetterTogether

# Compose multiple programs and optimize them together
optimizer = BetterTogether(
    programs={
        'qa_program': QAProgram(),
        'summarizer': SummarizerProgram(),
        'classifier': ClassifierProgram()
    },
    metric=combined_metric,
    weights={'qa_program': 0.5, 'summarizer': 0.3, 'classifier': 0.2}
)

# Optimizes all programs jointly
optimized_suite = optimizer.compile(trainset=trainset)
\`\`\`

### Hyperparameter Optimization

Find the best hyperparameters for the optimizer itself:

\`\`\`python
def optimize_optimizer_hyperparameters(program, trainset, valset, metric):
    """Find best hyperparameters for the optimizer itself."""
    
    import optuna
    
    def objective(trial):
        # Suggest hyperparameters
        max_demos = trial.suggest_int('max_demos', 1, 8)
        num_candidates = trial.suggest_int('num_candidates', 5, 20)
        temperature = trial.suggest_float('temperature', 0.4, 1.0)
        
        # Create optimizer with suggested params
        optimizer = BootstrapFewShotWithRandomSearch(
            metric=metric,
            max_bootstrapped_demos=max_demos,
            max_labeled_demos=max_demos,
            num_candidate_programs=num_candidates,
            temperature=temperature
        )
        
        # Compile and evaluate
        try:
            optimized = optimizer.compile(program, trainset=trainset)
            score = dspy.Evaluate(devset=valset, metric=metric)(optimized)
            return score
        except Exception as e:
            print(f"Trial failed: {e}")
            return 0.0
    
    # Run optimization
    study = optuna.create_study(direction='maximize')
    study.optimize(objective, n_trials=20)
    
    # Best parameters
    print(f"Best parameters: {study.best_params}")
    print(f"Best score: {study.best_value:.3f}")
    
    # Visualize optimization history
    import optuna.visualization as vis
    
    # Plot optimization history
    fig = vis.plot_optimization_history(study)
    fig.show()
    
    # Plot parameter importances
    fig = vis.plot_param_importances(study)
    fig.show()
    
    return study.best_params

# Example usage
best_params = optimize_optimizer_hyperparameters(
    program=QAProgram(),
    trainset=train_data[:50],
    valset=val_data,
    metric=accuracy_metric
)

# Use best parameters
final_optimizer = BootstrapFewShotWithRandomSearch(
    metric=accuracy_metric,
    **best_params
)
final_program = final_optimizer.compile(QAProgram(), trainset=full_trainset)
\`\`\`

### Optimization Debugging and Analysis

\`\`\`python
class OptimizationDebugger:
    """Debug and analyze optimization runs."""
    
    def __init__(self, optimizer):
        self.optimizer = optimizer
        self.traces = []
        self.proposals = []
        self.scores = []
    
    def debug_compile(self, program, trainset):
        """Compile with detailed debugging."""
        
        # Hook into optimizer internals
        original_methods = {}
        
        def track_proposal(proposal):
            self.proposals.append({
                'proposal': proposal,
                'timestamp': datetime.now()
            })
            return proposal
        
        def track_score(candidate, score):
            self.scores.append({
                'candidate': str(candidate)[:100],
                'score': score,
                'timestamp': datetime.now()
            })
            return score
        
        # Run optimization with tracking
        result = self.optimizer.compile(program, trainset)
        
        return result
    
    def analyze(self):
        """Analyze optimization run."""
        
        print(f"Total Proposals: {len(self.proposals)}")
        print(f"Total Evaluations: {len(self.scores)}")
        
        if self.scores:
            scores = [s['score'] for s in self.scores]
            
            plt.figure(figsize=(12, 4))
            
            # Score distribution
            plt.subplot(1, 3, 1)
            plt.hist(scores, bins=20, alpha=0.7, color='blue')
            plt.xlabel('Score')
            plt.ylabel('Count')
            plt.title('Score Distribution')
            
            # Score progression
            plt.subplot(1, 3, 2)
            plt.plot(scores, 'o-', markersize=4)
            plt.xlabel('Evaluation')
            plt.ylabel('Score')
            plt.title('Score Progression')
            
            # Best score over time
            plt.subplot(1, 3, 3)
            best_scores = np.maximum.accumulate(scores)
            plt.plot(best_scores, 'g-', linewidth=2)
            plt.xlabel('Evaluation')
            plt.ylabel('Best Score')
            plt.title('Best Score Over Time')
            
            plt.tight_layout()
            plt.show()
            
            # Print statistics
            print(f"\\nScore Statistics:")
            print(f"  Mean: {np.mean(scores):.3f}")
            print(f"  Std: {np.std(scores):.3f}")
            print(f"  Min: {np.min(scores):.3f}")
            print(f"  Max: {np.max(scores):.3f}")

# Usage
debugger = OptimizationDebugger(optimizer)
optimized = debugger.debug_compile(program, trainset)
debugger.analyze()
\`\`\`

### Choosing the Right Optimizer

\`\`\`python
# Decision tree for optimizer selection:

def choose_optimizer(task_type, data_size, budget, model_type):
    if data_size < 50:
        return "LabeledFewShot - Use your few examples directly"
    elif data_size < 200 and budget == "low":
        return "BootstrapFewShot - Fast and effective"
    elif model_type == "api_only":
        return "MIPROv2 - Best for API models"
    elif model_type == "fine_tunable" and budget == "high":
        return "BootstrapFinetune - Optimize weights"
    elif task_type == "complex" and budget == "high":
        return "Ensemble - Combine multiple approaches"
    else:
        return "BootstrapFewShotWithRandomSearch - Good default"

# Example usage
recommendation = choose_optimizer(
    task_type="qa",
    data_size=100,
    budget="medium",
    model_type="api_only"
)
print(recommendation)  # "MIPROv2 - Best for API models"
\`\`\`

## 💾 Saving and Loading Optimized Programs

### Serialization Format

\`\`\`python
# Save optimized program with all its optimized prompts and examples
optimized_program.save("my_optimized_qa.json")

# The saved file contains:
# - Optimized signature instructions
# - Selected few-shot examples
# - Module configuration
# - Optimization metadata

# Load optimized program
loaded_program = QAProgram()  # Create fresh instance
loaded_program.load("my_optimized_qa.json")  # Load optimized state

# Use loaded program immediately
result = loaded_program(question="What is DSPy?")
\`\`\`

### Version Control for Prompts

\`\`\`python
import json
from datetime import datetime

# Save with versioning
def save_with_version(program, base_name, metrics=None):
    timestamp = datetime.now().strftime("%Y%m%d_%H%M%S")
    filename = f"{base_name}_{timestamp}.json"
    
    # Save the program
    program.save(filename)
    
    # Save metadata
    metadata = {
        "timestamp": timestamp,
        "filename": filename,
        "metrics": metrics or {},
        "optimizer_used": getattr(program, '_optimizer_name', 'unknown')
    }
    
    with open(f"{base_name}_metadata.json", "w") as f:
        json.dump(metadata, f, indent=2)
    
    return filename

# Usage
filename = save_with_version(
    optimized_program, 
    "qa_model",
    metrics={"accuracy": 0.85, "cost_per_query": 0.02}
)
print(f"Saved as: {filename}")
\`\`\`

### Loading and Comparing Models

\`\`\`python
# Load and compare different optimized versions
def load_and_evaluate(filename, test_set, metric):
    program = QAProgram()
    program.load(filename)
    
    evaluator = dspy.Evaluate(devset=test_set, metric=metric)
    score = evaluator(program)
    
    return {
        'filename': filename,
        'score': score,
        'program': program
    }

# Compare multiple versions
versions = [
    "qa_model_20241201_143022.json",
    "qa_model_20241201_151545.json",
    "qa_model_20241201_163311.json"
]

results = []
for version in versions:
    result = load_and_evaluate(version, test_set, accuracy_metric)
    results.append(result)

# Find best version
best = max(results, key=lambda x: x['score'])
print(f"Best model: {best['filename']} with score: {best['score']}")
\`\`\`

### A/B Testing Framework

Run A/B tests between different optimized versions:

\`\`\`python
import random
from scipy import stats

class ABTestFramework:
    """Run A/B tests between different program versions."""
    
    def __init__(self):
        self.results = {
            'a': {'successes': 0, 'total': 0},
            'b': {'successes': 0, 'total': 0}
        }
    
    def run_test(self, program_a, program_b, test_queries, metric):
        """Run A/B test on test queries."""
        
        for query in test_queries:
            # Randomly assign to A or B
            if random.random() < 0.5:
                version = 'a'
                program = program_a
            else:
                version = 'b'
                program = program_b
            
            # Run and evaluate
            try:
                prediction = program(**query.inputs())
                success = metric(query, prediction) > 0.5
                
                self.results[version]['total'] += 1
                if success:
                    self.results[version]['successes'] += 1
            except Exception as e:
                print(f"Error in version {version}: {e}")
        
        return self.analyze_results()
    
    def analyze_results(self):
        """Statistical analysis of A/B test."""
        
        # Calculate success rates
        rate_a = self.results['a']['successes'] / max(self.results['a']['total'], 1)
        rate_b = self.results['b']['successes'] / max(self.results['b']['total'], 1)
        
        # Statistical significance test
        successes = [self.results['a']['successes'], self.results['b']['successes']]
        totals = [self.results['a']['total'], self.results['b']['total']]
        
        # Chi-square test
        if all(t > 0 for t in totals):
            chi2, p_value, _, _ = stats.chi2_contingency([
                successes,
                [totals[0] - successes[0], totals[1] - successes[1]]
            ])
        else:
            p_value = 1.0
        
        # Confidence intervals
        ci_a = self.wilson_confidence_interval(
            self.results['a']['successes'],
            self.results['a']['total']
        )
        ci_b = self.wilson_confidence_interval(
            self.results['b']['successes'],
            self.results['b']['total']
        )
        
        results = {
            'version_a': {
                'success_rate': rate_a,
                'confidence_interval': ci_a,
                'n': self.results['a']['total']
            },
            'version_b': {
                'success_rate': rate_b,
                'confidence_interval': ci_b,
                'n': self.results['b']['total']
            },
            'p_value': p_value,
            'significant': p_value < 0.05,
            'winner': 'A' if rate_a > rate_b else 'B'
        }
        
        self.visualize_results(results)
        
        return results
    
    def wilson_confidence_interval(self, successes, total, confidence=0.95):
        """Calculate Wilson score confidence interval."""
        
        if total == 0:
            return (0, 1)
        
        p = successes / total
        z = stats.norm.ppf(1 - (1 - confidence) / 2)
        
        denominator = 1 + z**2 / total
        centre = (p + z**2 / (2 * total)) / denominator
        offset = z * ((p * (1 - p) / total + z**2 / (4 * total**2)) ** 0.5) / denominator
        
        return (max(0, centre - offset), min(1, centre + offset))
    
    def visualize_results(self, results):
        """Visualize A/B test results."""
        
        import matplotlib.pyplot as plt
        
        fig, (ax1, ax2) = plt.subplots(1, 2, figsize=(12, 5))
        
        # Success rates with confidence intervals
        versions = ['Version A', 'Version B']
        rates = [results['version_a']['success_rate'], 
                results['version_b']['success_rate']]
        
        ci_lower = [results['version_a']['confidence_interval'][0],
                    results['version_b']['confidence_interval'][0]]
        ci_upper = [results['version_a']['confidence_interval'][1],
                    results['version_b']['confidence_interval'][1]]
        
        errors = [[rates[i] - ci_lower[i] for i in range(2)],
                  [ci_upper[i] - rates[i] for i in range(2)]]
        
        bars = ax1.bar(versions, rates, yerr=errors, capsize=10)
        bars[0].set_color('blue')
        bars[1].set_color('orange')
        
        ax1.set_ylabel('Success Rate')
        ax1.set_title(f'A/B Test Results (p={results["p_value"]:.3f})')
        ax1.set_ylim(0, 1)
        
        # Add significance marker
        if results['significant']:
            ax1.text(0.5, 0.95, '★ Significant Difference', 
                    transform=ax1.transAxes, ha='center',
                    bbox=dict(boxstyle='round', facecolor='yellow', alpha=0.5))
        
        # Sample sizes
        sizes = [results['version_a']['n'], results['version_b']['n']]
        ax2.bar(versions, sizes)
        ax2.set_ylabel('Sample Size')
        ax2.set_title('Test Distribution')
        
        plt.tight_layout()
        plt.show()

# Usage example
ab_test = ABTestFramework()

# Load two optimized programs
program_a = QAProgram()
program_a.load("optimized_bootstrap.json")

program_b = QAProgram()
program_b.load("optimized_mipro.json")

# Run A/B test
results = ab_test.run_test(
    program_a=program_a,
    program_b=program_b,
    test_queries=test_set,
    metric=accuracy_metric
)

print(f"Winner: Version {results['winner']}")
print(f"Statistical significance: p={results['p_value']:.4f}")
\`\`\`

## 📋 Module Summary

### Key Optimization Strategies Covered

✅ **Few-Shot Learning Optimizers**
- LabeledFewShot: Direct use of manual examples
- BootstrapFewShot: Self-generating high-quality examples
- BootstrapFewShotWithRandomSearch: Enhanced with random search
- KNNFewShot: Dynamic similarity-based example selection

✅ **Instruction Optimization**
- COPRO: Coordinate ascent for prompt optimization
- MIPROv2: Advanced data-aware optimization with multiple modes
- Zero-shot optimization capabilities

✅ **Advanced Techniques**
- BootstrapFinetune: Weight optimization for fine-tunable models
- Ensemble: Combining multiple optimized programs
- BetterTogether: Sophisticated optimizer composition

✅ **Practical Considerations**
- Cost and time optimization strategies
- Proper data splitting for training/validation
- Model saving, loading, and version control
- Optimizer selection guidelines

### Optimization Best Practices

1. **Start Simple**: Begin with BootstrapFewShot for most tasks
2. **Consider Budget**: Balance optimization cost with performance gains
3. **Proper Evaluation**: Always use separate validation/test sets
4. **Version Control**: Save and track your optimized models
5. **Ensemble When Needed**: Combine strategies for critical applications
6. **Monitor Costs**: Optimization can be expensive - plan accordingly

### What's Next?

In Module 5, we'll explore:
- Advanced DSPy programming patterns
- Caching and performance optimization
- Observability and debugging techniques
- Working with different data types

You now have the knowledge to optimize DSPy programs effectively and choose the right optimization strategy for your specific needs!`,
  exercises: [
    {
      id: 'dspy-m4-ex1',
      title: 'Optimization Comparison',
      description: 'Compare different optimizers on the same task',
      difficulty: 'hard',
      type: 'code',
      instructions:
        'Create a comparison study between BootstrapFewShot and LabeledFewShot optimizers.',
      hints: ['Use the same training data', 'Apply same evaluation metric', 'Compare performance'],
      solution: `# Comprehensive optimizer comparison study
def compare_optimizers(program, trainset, devset, metric):
    import time
    results = {}
    
    optimizers = {
        'LabeledFewShot': dspy.LabeledFewShot(k=4),
        'BootstrapFewShot': dspy.BootstrapFewShot(metric=metric, max_bootstrapped_demos=4),
        'COPRO': dspy.COPRO(metric=metric, breadth=5, depth=2),
        'MIPROv2_Light': dspy.MIPROv2(metric=metric, num_candidates=10)
    }
    
    for name, optimizer in optimizers.items():
        start_time = time.time()
        try:
            optimized_prog = optimizer.compile(program, trainset=trainset)
            score = dspy.Evaluate(devset=devset, metric=metric)(optimized_prog)
            optimization_time = time.time() - start_time
            
            results[name] = {
                'score': score,
                'time': optimization_time,
                'success': True
            }
        except Exception as e:
            results[name] = {
                'score': 0.0,
                'time': time.time() - start_time,
                'success': False,
                'error': str(e)
            }
    
    return results`,
      testCases: [
        {
          input: 'compare_optimizers function',
          expected: true,
          description: 'Optimizer comparison function',
        },
      ],
    },
    {
      id: 'dspy-m4-ex2',
      title: 'Custom Example Selection',
      description: 'Implement custom example selection for KNNFewShot',
      difficulty: 'hard',
      type: 'code',
      instructions:
        'Create a custom example selection strategy that considers both similarity and diversity.',
      hints: [
        'Use embeddings for similarity',
        'Balance similarity with diversity',
        'Implement as a custom selector',
      ],
      solution: `import numpy as np
from sklearn.metrics.pairwise import cosine_similarity

class DiverseKNNSelector:
    """Select examples that are similar but also diverse."""
    
    def __init__(self, k=5, diversity_weight=0.3):
        self.k = k
        self.diversity_weight = diversity_weight
        self.embedder = dspy.SentenceTransformers("all-MiniLM-L6-v2")
    
    def select_examples(self, query, examples):
        # Embed query and examples
        query_emb = self.embedder.encode([query])
        example_texts = [ex.question for ex in examples]
        example_embs = self.embedder.encode(example_texts)
        
        # Calculate similarities
        similarities = cosine_similarity(query_emb, example_embs)[0]
        
        selected = []
        selected_indices = []
        
        for _ in range(min(self.k, len(examples))):
            scores = []
            
            for i, (sim, ex) in enumerate(zip(similarities, examples)):
                if i in selected_indices:
                    continue
                
                # Similarity score
                sim_score = sim
                
                # Diversity score (distance from already selected)
                if selected_indices:
                    selected_embs = example_embs[selected_indices]
                    diversity = 1 - np.max(cosine_similarity([example_embs[i]], selected_embs))
                else:
                    diversity = 1.0
                
                # Combined score
                score = (1 - self.diversity_weight) * sim_score + self.diversity_weight * diversity
                scores.append((score, i, ex))
            
            # Select best
            if scores:
                best = max(scores, key=lambda x: x[0])
                selected.append(best[2])
                selected_indices.append(best[1])
        
        return selected

# Usage with KNNFewShot
selector = DiverseKNNSelector(k=5, diversity_weight=0.3)
custom_knn = KNNFewShot(
    k=5,
    trainset=trainset,
    example_selector=selector.select_examples
)`,
      testCases: [
        {
          input: 'DiverseKNNSelector class',
          expected: true,
          description: 'Custom KNN selector implementation',
        },
      ],
    },
    {
      id: 'dspy-m4-ex3',
      title: 'Optimization Pipeline',
      description: 'Build an optimization pipeline with 3 stages',
      difficulty: 'hard',
      type: 'code',
      instructions:
        'Create a multi-stage optimization pipeline that progressively improves a program.',
      hints: ['Start with bootstrap', 'Then optimize instructions', 'Consider ensemble at the end'],
      solution: `class OptimizationPipeline:
    """Multi-stage optimization pipeline."""
    
    def __init__(self, metric):
        self.metric = metric
        self.stages = []
        self.results = []
    
    def add_stage(self, name, optimizer, description=""):
        """Add an optimization stage."""
        self.stages.append({
            'name': name,
            'optimizer': optimizer,
            'description': description
        })
        return self
    
    def compile(self, program, trainset, valset=None):
        """Run the full pipeline."""
        current_program = program
        
        print("Starting Optimization Pipeline")
        print("=" * 50)
        
        for i, stage in enumerate(self.stages):
            print(f"\\nStage {i+1}: {stage['name']}")
            print(f"Description: {stage['description']}")
            
            # Run optimization
            start_time = time.time()
            optimized = stage['optimizer'].compile(
                current_program.deepcopy(),
                trainset=trainset
            )
            optimization_time = time.time() - start_time
            
            # Evaluate if valset provided
            if valset:
                evaluator = dspy.Evaluate(devset=valset, metric=self.metric)
                score = evaluator(optimized)
                print(f"Validation Score: {score:.3f}")
            else:
                score = None
            
            # Store results
            self.results.append({
                'stage': stage['name'],
                'program': optimized,
                'score': score,
                'time': optimization_time
            })
            
            # Update current program
            current_program = optimized
            
            print(f"Time taken: {optimization_time:.2f}s")
        
        print("\\nPipeline Complete!")
        self.print_summary()
        
        return current_program
    
    def print_summary(self):
        """Print optimization summary."""
        print("\\nOptimization Summary:")
        print("-" * 40)
        
        for result in self.results:
            print(f"{result['stage']:20} | Score: {result['score']:.3f if result['score'] else 'N/A':6} | Time: {result['time']:.2f}s")
        
        if any(r['score'] for r in self.results):
            best_stage = max(
                (r for r in self.results if r['score']), 
                key=lambda x: x['score']
            )
            print(f"\\nBest Stage: {best_stage['stage']} (Score: {best_stage['score']:.3f})")

# Build and run pipeline
pipeline = OptimizationPipeline(metric=accuracy_metric)

pipeline.add_stage(
    "Bootstrap",
    BootstrapFewShot(metric=accuracy_metric, max_bootstrapped_demos=3),
    "Generate initial few-shot examples"
)

pipeline.add_stage(
    "Instruction Optimization",
    COPRO(metric=accuracy_metric, breadth=10, depth=2),
    "Optimize prompt instructions"
)

pipeline.add_stage(
    "Final Refinement",
    MIPROv2(metric=accuracy_metric, num_candidates=15),
    "Data-aware final optimization"
)

# Run pipeline
final_program = pipeline.compile(
    program=QAProgram(),
    trainset=train_data,
    valset=val_data
)`,
      testCases: [
        {
          input: 'OptimizationPipeline class',
          expected: true,
          description: 'Multi-stage optimization pipeline',
        },
      ],
    },
    {
      id: 'dspy-m4-ex4',
      title: 'Prompt Version Control',
      description: 'Create a prompt version control system with diff visualization',
      difficulty: 'hard',
      type: 'code',
      instructions:
        'Build a version control system that tracks prompt changes and visualizes differences.',
      hints: ['Store prompts with metadata', 'Implement diff functionality', 'Add visualization'],
      solution: `import json
import difflib
from datetime import datetime
import os

class PromptVersionControl:
    """Version control system for DSPy prompts."""
    
    def __init__(self, storage_dir="./prompt_versions"):
        self.storage_dir = storage_dir
        os.makedirs(storage_dir, exist_ok=True)
        self.version_index = self.load_index()
    
    def load_index(self):
        """Load or create version index."""
        index_path = os.path.join(self.storage_dir, "index.json")
        if os.path.exists(index_path):
            with open(index_path, 'r') as f:
                return json.load(f)
        return {"versions": [], "current": None}
    
    def save_index(self):
        """Save version index."""
        with open(os.path.join(self.storage_dir, "index.json"), 'w') as f:
            json.dump(self.version_index, f, indent=2)
    
    def commit(self, program, message, metadata=None):
        """Save a new version of the program."""
        version_id = datetime.now().strftime("%Y%m%d_%H%M%S")
        
        # Extract all prompts and configurations
        prompts = self.extract_prompts(program)
        
        # Create version entry
        version = {
            "id": version_id,
            "timestamp": datetime.now().isoformat(),
            "message": message,
            "prompts": prompts,
            "metadata": metadata or {},
            "parent": self.version_index["current"]
        }
        
        # Save version file
        version_path = os.path.join(self.storage_dir, f"{version_id}.json")
        with open(version_path, 'w') as f:
            json.dump(version, f, indent=2)
        
        # Save program state
        program.save(os.path.join(self.storage_dir, f"{version_id}_program.json"))
        
        # Update index
        self.version_index["versions"].append(version_id)
        self.version_index["current"] = version_id
        self.save_index()
        
        print(f"Committed version: {version_id}")
        print(f"Message: {message}")
        
        return version_id
    
    def extract_prompts(self, program):
        """Extract all prompts from a program."""
        prompts = {}
        
        for name, module in program.named_modules():
            if hasattr(module, 'extended_signature'):
                sig = module.extended_signature
                prompts[name] = {
                    "signature": str(module.signature),
                    "instructions": getattr(sig, 'instructions', ''),
                    "demos": len(getattr(module, 'demos', [])),
                    "fields": {
                        field: str(getattr(sig, field, ''))
                        for field in sig.fields
                    }
                }
        
        return prompts
    
    def diff(self, version1_id, version2_id=None):
        """Show differences between two versions."""
        # Load versions
        if version2_id is None:
            version2_id = self.version_index["current"]
        
        v1_path = os.path.join(self.storage_dir, f"{version1_id}.json")
        v2_path = os.path.join(self.storage_dir, f"{version2_id}.json")
        
        with open(v1_path, 'r') as f:
            v1 = json.load(f)
        with open(v2_path, 'r') as f:
            v2 = json.load(f)
        
        print(f"\\nDiff: {version1_id} -> {version2_id}")
        print(f"From: {v1['message']} ({v1['timestamp']})")
        print(f"To:   {v2['message']} ({v2['timestamp']})")
        print("=" * 60)
        
        # Compare prompts
        all_modules = set(v1['prompts'].keys()) | set(v2['prompts'].keys())
        
        for module in sorted(all_modules):
            if module not in v1['prompts']:
                print(f"\\n+ Added module: {module}")
                self.print_prompt_details(v2['prompts'][module], prefix="+ ")
            elif module not in v2['prompts']:
                print(f"\\n- Removed module: {module}")
                self.print_prompt_details(v1['prompts'][module], prefix="- ")
            else:
                # Check for changes
                p1 = v1['prompts'][module]
                p2 = v2['prompts'][module]
                
                if p1 != p2:
                    print(f"\\n~ Modified module: {module}")
                    
                    # Show instruction diff
                    if p1.get('instructions') != p2.get('instructions'):
                        print("  Instructions changed:")
                        self.show_text_diff(
                            p1.get('instructions', ''),
                            p2.get('instructions', '')
                        )
                    
                    # Show demo count change
                    if p1.get('demos') != p2.get('demos'):
                        print(f"  Demos: {p1.get('demos', 0)} -> {p2.get('demos', 0)}")
    
    def show_text_diff(self, text1, text2):
        """Show colored diff of two texts."""
        diff = difflib.unified_diff(
            text1.splitlines(keepends=True),
            text2.splitlines(keepends=True),
            n=3
        )
        
        for line in diff:
            if line.startswith('+'):
                print(f"\\033[92m{line}\\033[0m", end='')  # Green
            elif line.startswith('-'):
                print(f"\\033[91m{line}\\033[0m", end='')  # Red
            else:
                print(line, end='')
    
    def print_prompt_details(self, prompt, prefix=""):
        """Print prompt details with prefix."""
        print(f"{prefix}Signature: {prompt.get('signature', 'N/A')}")
        print(f"{prefix}Instructions: {prompt.get('instructions', 'N/A')[:100]}...")
        print(f"{prefix}Demos: {prompt.get('demos', 0)}")
    
    def log(self, n=10):
        """Show version history."""
        print("\\nVersion History:")
        print("-" * 60)
        
        versions = self.version_index["versions"][-n:]
        for version_id in reversed(versions):
            v_path = os.path.join(self.storage_dir, f"{version_id}.json")
            with open(v_path, 'r') as f:
                v = json.load(f)
            
            current = " (current)" if version_id == self.version_index["current"] else ""
            print(f"{version_id}{current}")
            print(f"  {v['message']}")
            print(f"  {v['timestamp']}")
            if v.get('metadata'):
                print(f"  Metadata: {v['metadata']}")
            print()

# Usage example
vc = PromptVersionControl()

# Initial commit
vc.commit(initial_program, "Initial QA program")

# After optimization
optimized = optimizer.compile(initial_program, trainset)
vc.commit(
    optimized, 
    "Applied BootstrapFewShot optimization",
    metadata={"optimizer": "BootstrapFewShot", "score": 0.85}
)

# After another optimization
mipro_optimized = mipro_optimizer.compile(optimized, trainset)
vc.commit(
    mipro_optimized,
    "Applied MIPROv2 optimization",
    metadata={"optimizer": "MIPROv2", "score": 0.92}
)

# View history
vc.log()

# Show diff
vc.diff(vc.version_index["versions"][-2], vc.version_index["versions"][-1])`,
      testCases: [
        {
          input: 'PromptVersionControl class',
          expected: true,
          description: 'Version control system for prompts',
        },
      ],
    },
  ],
  quiz: {
    id: 'dspy-m4-quiz',
    title: 'Module 4: DSPy Optimization - Knowledge Check',
    description: 'Test your understanding of DSPy optimization techniques',
    passingScore: 4,
    questions: [
      {
        id: 'q1',
        question: 'What does BootstrapFewShot optimize?',
        choices: [
          'Only model weights',
          'Only prompts',
          'Few-shot examples and prompts',
          'Only hyperparameters',
        ],
        correctAnswer: 2,
        explanation: 'BootstrapFewShot optimizes both few-shot examples and prompts automatically.',
      },
      {
        id: 'q2',
        question: 'Which optimizer is best for zero-shot optimization?',
        choices: ['BootstrapFewShot', 'LabeledFewShot', 'MIPROv2', 'COPRO'],
        correctAnswer: 2,
        explanation:
          'MIPROv2 can perform zero-shot optimization by focusing on instruction optimization.',
      },
      {
        id: 'q3',
        question: 'What is the main advantage of ensemble methods?',
        choices: [
          'Faster inference',
          'Lower memory usage',
          'Improved robustness and accuracy',
          'Simpler implementation',
        ],
        correctAnswer: 2,
        explanation:
          'Ensemble methods combine multiple programs to improve robustness and accuracy.',
      },
      {
        id: 'q4',
        question: 'How do you save an optimized DSPy program?',
        choices: [
          'program.export("file.json")',
          'program.save("file.json")',
          'dspy.save(program, "file.json")',
          'pickle.dump(program, file)',
        ],
        correctAnswer: 1,
        explanation: 'DSPy programs use the .save() method to serialize their optimized state.',
      },
      {
        id: 'q5',
        question: 'What does COPRO stand for?',
        choices: [
          'Coordinate Prompts',
          'Coordinate Ascent for Prompts',
          'Collaborative Prompt Optimization',
          'Continuous Prompt Refinement',
        ],
        correctAnswer: 1,
        explanation: 'COPRO stands for Coordinate Ascent for Prompts, an optimization technique.',
      },
    ],
  },
};
